import React from 'react';
import { DateTime } from 'luxon';
import classNames from 'classnames';

import { useTranslation } from 'react-i18next';
import styles from './AnalystRatingBar.module.scss';

export interface AnalystRatingBarData {
  date: string;
  ratingBuyCount: number;
  ratingCountTotal: number;
  ratingHoldCount: number;
  ratingSellCount: number;
  ratingStrongBuyCount: number;
  ratingStrongSellCount: number;
}

export interface AnalystRatingBarProps extends AnalystRatingBarData {
  type: 'vertical' | 'horizontal';
  placeholder?: boolean;
}

export const AnalystRatingBar = ({
  date,
  placeholder = false,
  ratingBuyCount,
  ratingCountTotal,
  ratingHoldCount,
  ratingSellCount,
  ratingStrongBuyCount,
  ratingStrongSellCount,
  type,
}: AnalystRatingBarProps) => {
  const { t, i18n } = useTranslation('quote');
  const isVertical = type === 'vertical';

  const ratings: { className: string; label: string; value: number }[] = [
    !isVertical && { className: 'strong_buy', label: t('Market.Actions.strong-buy'), value: ratingStrongBuyCount },
    {
      className: 'buy',
      label: t('Market.Actions.buy'),
      value: isVertical ? ratingStrongBuyCount + ratingBuyCount : ratingBuyCount,
    },
    { className: 'hold', label: t('Market.Actions.hold'), value: ratingHoldCount },
    { className: 'sell', label: t('Market.Actions.sell'), value: ratingSellCount },
    { className: 'strong_sell', label: t('Market.Actions.string-sell'), value: ratingStrongSellCount },
  ].filter(rating => !!rating) as { className: string; label: string; value: number }[];

  const wrapperClass = isVertical ? styles.analystRatingVerticalBarsWrapper : styles.analystRatingHorizontalBarsWrapper;

  if (isVertical) {
    return (
      <div
        className={classNames(wrapperClass, 'flex flex-col items-center h-full w-full w-[48px] md:w-[50px] rounded overflow-hidden', {
          'missing-data': placeholder || ratingCountTotal === 0,
        })}
      >
        {ratings.map(rating => {
          return (
            <div
              className={`color-box ${rating.className}`}
              key={rating.label}
              style={{
                height:
                  ratingCountTotal === 0
                    ? '20%'
                    : rating.value === 0
                      ? '0'
                      : `${(rating.value / ratingCountTotal) * 100}%`,
              }}
            >
              <span className="leading-none">{ratingCountTotal === 0 ? 0 : rating.value > 0 ? rating.value : ' '}</span>
            </div>
          );
        })}
        <div className="font-normal whitespace-nowrap text-center text-sm p-2 text-gray-700 mt-auto">
          {new Date(date).getFullYear() !== new Date().getFullYear()
            ? DateTime.fromFormat(date, 'LLL/yyyy').setLocale(i18n.language).toFormat('LLL yy')
            : DateTime.fromFormat(date, 'LLL/yyyy').setLocale(i18n.language).toFormat('LLL')}
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(wrapperClass, 'flex items-center w-full', { 'missing-data': placeholder || ratingCountTotal === 0 })}
    >
      <div className="font-bold mr-2 date-label">
        {/* If the date is not in the same year so the 2 digit year */}
        {new Date(date).getFullYear() !== new Date().getFullYear()
          ? DateTime.fromFormat(date, 'LLL/yyyy').toFormat('LLL yy')
          : DateTime.fromFormat(date, 'LLL/yyyy').toFormat('LLL')}
      </div>
      {ratings.map(rating => {
        return (
          <div
            className={`color-box ${rating.className}`}
            key={rating.label}
            style={{
              width:
                ratingCountTotal === 0
                  ? '20%'
                  : rating.value === 0
                    ? '0'
                    : `${(rating.value / ratingCountTotal) * 100}%`,
            }}
          >
            <span className="leading-none">{ratingCountTotal === 0 ? 0 : rating.value > 0 ? rating.value : ' '}</span>
          </div>
        );
      })}
    </div>
  );
};

export default AnalystRatingBar;
